import { arrivalInto, firedSlotOf, flowEventRules, resolveFlowOutcome, type AttemptTally, type FeedbackState, type FlowCondition, type RefCensusTask } from '@agavi/blocks';
import { ArrowRight, Check, Dices, RotateCcw, ScanEye, X } from 'lucide-react';
import { Fragment, useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import type { TestBarCategory, TestBarChip } from '@/components/task/collapsed-input-block';
import type { FiredStatesRegistry } from '@/components/task/fired-states';
import type { ConditionChips } from '@/lib/condition-chips';
import type { Block } from '@/types/experience';

import { usePreviewSession, useRecallStandIns, useRunSession } from '@/components/preview-session-context';
import { aliasChipName, useAnswerRefScope } from '@/components/task/answer-ref-context';
import { ANSWERABLE_BLOCK_TYPES, BLOCK_META } from '@/components/task/block-library';
import { LegendChip } from '@/components/task/collapsed-input-block';
import { ConditionChipRow } from '@/components/task/condition-chips';
import { changeTitle } from '@/components/task/flow-change-card';
import { HEADING_CLASS, HEADING_CLASS_LG } from '@/components/task/options-section';
import { PreviewConnectionsDiagram } from '@/components/task/preview-connections-diagram';
import { recallFeeders } from '@/components/task/recall-feeders';
import { useIsPhone } from '@/components/task/use-viewport';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import SelectMenu from '@/components/ui/select-menu';
import { stateChipsFor } from '@/lib/block-state-labels';

/**
 * What the task is doing behind the scenes, for the teacher walking their own experience.
 *
 * ON DESKTOP IT IS SIMPLY OPEN, with no control to summon it: it describes the task already on
 * screen, so a teacher wants it for the whole walk rather than once per question, and the one
 * row it could have lived in has no space for a fourth button. The read-through covers it while
 * that is up. A phone has nowhere to dock it, so there it is a sheet with a door — which is why
 * `onClose` is optional rather than required.
 *
 * TEACHER FURNITURE, like Reroll and the sensor simulator: every mount is wrapped
 * `data-teacher-only`, which keeps it out of the student runner and out of the screen reader
 * emulator's read-through. A student never meets this, so the emulator describing it would be
 * describing something that does not exist for the person it is emulating.
 *
 * Shares the Screen Reader Emulator's shell deliberately rather than inventing a second one:
 * `PreviewA11yPanel` already settled where an inspection panel lives on each surface — the slot
 * Variables and Flow vacate on entering Preview, a full-screen sheet on a phone — and there is
 * exactly ONE such slot on desktop. That is why the two are mutually exclusive rather than
 * stacked: `a11yPanel` is a single choice, so opening one closes the other by construction and
 * neither has to know the other exists.
 */

const NO_TALLY: AttemptTally = { all: 0, complete: 0, unique: 0, incorrect: 0 };

/* Module-level so their identity is stable: `useSyncExternalStore` resubscribes on a new
   `subscribe`, and re-reads forever on a `getSnapshot` that returns a fresh object. */
const EMPTY_CHIPS: Record<string, TestBarChip[]> = {};
/* The SAME object every call. Returning `{}` here is a fresh object each time, which
   `useSyncExternalStore` reads as "changed" on every render -- an infinite loop, and the first
   thing the tests caught. */
const NO_CHIPS = () => EMPTY_CHIPS;
const EMPTY_STATES: Record<string, FeedbackState[]> = {};
const NO_STATES = () => EMPTY_STATES;
const NEVER_CHANGES = () => () => {};

/** The aim every task has, and the one the block rows start pointed at. */
const ALL_CORRECT = 'allCorrect';

/**
 * WHETHER TWO SETS OF FIRED STATES ARE THE SAME ANSWER.
 *
 * By state id per block, sorted. The states are rebuilt on every evaluation so object identity
 * says nothing, and a `JSON.stringify` compare would call two equal maps different because
 * their keys came out in a different order -- which is a bug this codebase has already paid
 * for once.
 */
function sameResults(
  a: Record<string, FeedbackState[]>,
  b: Record<string, FeedbackState[]>,
): boolean {
  const ids = (m: Record<string, FeedbackState[]>, k: string) =>
    (m[k] ?? []).map(s => s.id).sort().join('\u0000');
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (ids(a, k) !== ids(b, k)) return false;
  return true;
}

/**
 * The four filters, in the order every attempts condition lists them.
 *
 * Reusing the condition editor's own keys rather than adding title-cased copies: the panel and the
 * rule a teacher writes against it must name an attempt the same way, and two sets of words for
 * one concept is exactly how they stop agreeing. The casing difference is presentational — the
 * condition reads them inline in a sentence, the tile stands alone — so it belongs in CSS.
 */
const FILTERS = ['all', 'complete', 'unique', 'incorrect'] as const;

/**
 * Written out rather than built as `attemptsFilter.${f}`.
 *
 * A key assembled at runtime cannot be found by grepping for it, which is why `invariants.test.ts`
 * keeps a ceiling on how many exist. Four known-at-compile-time keys have no business spending one
 * of those: the loop below still reads from one place, and a locale sweep can still see the keys.
 */
const FILTER_KEYS: Record<(typeof FILTERS)[number], string> = {
  all: 'task.flow.conditions.attemptsFilter.all',
  complete: 'task.flow.conditions.attemptsFilter.complete',
  unique: 'task.flow.conditions.attemptsFilter.unique',
  incorrect: 'task.flow.conditions.attemptsFilter.incorrect',
};

export function PreviewDebugPanel({ taskId, tasks, chipRegistry, onReroll, onReset, onClose, onRecallHover, fullScreen, short }: {
  /** Which task the readout is about. The run may hold many; this panel describes one. */
  taskId: string;
  /** The whole experience, because half of what this says is about the task's NEIGHBOURS. */
  tasks: readonly RefCensusTask[];
  /**
   * The blocks' own report of what they are asking.
   *
   * Handed in rather than taken from context: the provider wraps the student surface and this
   * panel sits in the column BESIDE it, so a context read here finds nothing. The page owns the
   * registry either way, so passing it is both shorter and honest about who is talking to whom.
   */
  chipRegistry?: FiredStatesRegistry | null;
  /**
   * The two run controls, moved here off the submit bar.
   *
   * Neither is a student control: one draws a different student's variant and the other puts
   * the run back to nothing, and a student meets neither. They belong beside the tally they
   * reset, which is also the only readout that shows they worked. Reroll is absent on a task
   * with nothing to draw -- there is no other variant to go to.
   */
  onReroll?: () => void;
  onReset?: () => void;
  /** Absent where the panel is the default occupant of its slot: there is nothing to close it to. */
  onClose?: () => void;
  /**
   * "WHAT DOES THIS ONE FEED?", answered on the task rather than in the band.
   *
   * A recall's reach is a set of blocks and the band is a list of names, so the answer cannot be
   * drawn here; the page rings the blocks instead. Absent where the panel covers the task it
   * would be pointing at, which is the portrait sheet.
   */
  onRecallHover?: (aliasId: string | null) => void;
  /** Phone: the panel IS the screen, so it takes the app's sheet titlebar. */
  fullScreen?: boolean;
  /**
   * TOO SHORT TO SPEND HEIGHT ON FURNITURE — the landscape dock, and nothing else.
   *
   * Measured there: 275px of panel, of which the titlebar and the anchored attempts bar took
   * 203 and left 72 for the content. Furniture outweighed the readout nearly three to one, and
   * the map and the blocks sat below a 72px window.
   *
   * So the attempts bar joins the scroller here. The anchoring exists so a long block list
   * cannot push the attempt counts off the bottom — and in a 320px dock the teacher is
   * scrolling anyway, so the guarantee it protects does not hold. Everywhere taller keeps it.
   */
  short?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { counts, visitCount, session } = usePreviewSession();
  /*
   * Subscribed, not read. The registry is refs so a block reporting does not re-render the task;
   * a reader therefore has to be woken, and `useSyncExternalStore` is the hook that does it
   * without tearing between what the blocks have said and what this draws.
   */
  const draftStates = useSyncExternalStore(
    chipRegistry?.subscribeChips ?? NEVER_CHANGES,
    chipRegistry?.readDraftStates ?? NO_STATES,
    chipRegistry?.readDraftStates ?? NO_STATES,
  );
  const reported = useSyncExternalStore(
    chipRegistry?.subscribeChips ?? NEVER_CHANGES,
    chipRegistry?.readChips ?? NO_CHIPS,
    chipRegistry?.readChips ?? NO_CHIPS,
  );

  /**
   * WHICH EVENT THE BLOCK ROWS ARE AIMED AT.
   *
   * NOT THE WHOLE LADDER. It used to be, and the ladder is the wrong list to put in front of a
   * teacher: No answer and Default are self-evident -- leave it blank, or get it wrong in any
   * of the hundred available ways -- and the sensor rungs are not reachable by typing at all.
   * Four of the six options were noise, and the two that were not are the two a teacher is
   * actually here to test.
   *
   * So: All Correct, the special events, and the changes. Held by id rather than by index
   * because a reorder in Flow must not silently move the aim onto a different rule.
   */
  const [targetId, setTargetId] = useState(ALL_CORRECT);

  /**
   * THE RECALLS THIS TASK IS WAITING ON, and the way to answer them by hand.
   *
   * A task that quotes an earlier answer does not work until that answer exists, so a teacher
   * dropped into the middle of an experience meets a sentence full of dimmed names. The chips
   * are settable in the prose -- click one and it cycles -- but nothing SAYS they are there,
   * which is the half this band adds: the feeders in one list, named, with what each is set to.
   *
   * `recallFeeders` decides membership, and the task surface rings the same rows, so the band
   * and the highlight cannot disagree about which references are outstanding. Its note explains
   * why a filled row stays.
   */
  /* Every control in here is finger-sized on a phone and compact everywhere else. The panel is
     the same component on all three surfaces, so the size is a property of the DEVICE rather
     than of the mount -- a `short` dock on a tablet is still a mouse. */
  const phone = useIsPhone();

  /*
   * NARROW IS A PROPERTY OF THE MOUNT, not of the device.
   *
   * `phone` above is deliberately a DEVICE query — controls are finger-sized on a phone and
   * compact everywhere else, and a short dock on a tablet is still a mouse. That is right for hit
   * targets and wrong for anything about WIDTH: the landscape dock is 320px whatever is pointing
   * at it, so keying the attempts bar's size to `phone` left it wrapping for anyone whose browser
   * was merely sized like a phone.
   *
   * ONLY THE LANDSCAPE DOCK. Measured, the full treatment wants about 373px for its four pairs
   * and the dock leaves 304 inside its padding — but the portrait sheet has the width of the
   * phone and does not need the help. The owner's call, and it is the right one: shrinking type
   * where it already fits buys nothing and costs legibility.
   */
  const tight = short;

  const refScope = useAnswerRefScope();
  const runSession = useRunSession();
  const { standIns, set: setStandIn } = useRecallStandIns();

  const feeders = useMemo(
    () => (setStandIn ? recallFeeders(refScope, runSession, taskId, a => aliasChipName(a, refScope!, t)) : []),
    [refScope, runSession, setStandIn, taskId, t]);

  const attempts = session?.attempts ?? [];
  const arrival = arrivalInto(tasks, taskId, attempts);
  /**
   * WHAT FIRED, until the student starts changing their answer.
   *
   * `firedSlotOf` answers for the last submission on this task, stay or exit. That is the right
   * answer the moment Submit is pressed and the wrong one a keystroke later: a teacher who goes
   * back to re-answer is building a NEW submission, and a mark reading as a result while they
   * work is a result about an answer that no longer exists.
   *
   * So the fired mark holds only while the draft still matches what was handed in. Change
   * anything and the drawing reverts to forecasting, which is what it should be doing while an
   * answer is being written.
   *
   * Compared on the fired state IDS per block, not on the objects and not on a stringify: the
   * states are rebuilt every evaluation so identity says nothing, and a key-order difference
   * between two equal maps is not a change of answer.
   */
  const submitted = [...attempts].reverse().find(a => a.taskId === taskId);
  const answerStands = !!submitted && sameResults(submitted.results, draftStates);
  const firedSlot = answerStands ? firedSlotOf(taskId, attempts) : null;

  /**
   * WHERE THIS ANSWER WOULD SEND THEM, asked of the REAL ladder.
   *
   * `resolveFlowOutcome` is the function Submit itself calls, so this cannot disagree with what
   * then happens -- a predictor written beside it could. The counts are taken WITH the draft as
   * a pending attempt, because an attempts condition has to see the try being predicted: "after
   * 3 attempts, offer a hint" fires ON the third, and a count from the log alone is a try short.
   *
   * Only while the student is mid-answer. Once a submission's own rung is being shown, that IS
   * the answer and a prediction beside it would be two claims about one submission.
   */
  const flow = tasks.find(x => x.id === taskId)?.flow;
  const predicted = !firedSlot && flow && Object.keys(draftStates).length > 0
    ? resolveFlowOutcome(flow, draftStates, counts(taskId, { results: draftStates, signatures: {} }) ?? undefined).slot
    : null;

  /*
   * NO ROUTE LINE. "Goes to X" sat above the diagram until the diagram started saying it
   * better: the exit card for X lights up and wears a play arrow, the task card goes clear.
   * A sentence naming the same destination is the fact twice, and the one a teacher reads
   * past. The forecast still exists -- `predicted` is what the diagram draws from -- it just
   * has no prose of its own any more.
   */
  /**
   * THE TARGET, AND WHAT IT WANTS OF EACH BLOCK.
   *
   * A reversal of what the rest of the panel does. Everything else reports forwards -- here is
   * what you typed, here is what fires. This answers the question a teacher actually has while
   * testing, which is the other way round: I want to see THAT event fire, so what do I type?
   *
   * All Correct is the default because it is the one target every task has and the one a
   * teacher checks first. A change sits in the same list as an event although it routes
   * nowhere: from here they are the same kind of thing, a set of conditions to satisfy, and
   * which of them a teacher is chasing is not this panel's business.
   */
  const rules = flow ? flowEventRules(flow) : [];
  const changes = flow?.changes ?? [];
  const allBlocks = (tasks.find(x => x.id === taskId)?.blocks ?? []) as Block[];
  /*
   * GROUPED, because the list holds two kinds of thing. A special event routes the student
   * somewhere; a change alters the task and leaves them where they are. From the block rows
   * they are identical -- a set of conditions to satisfy -- but a teacher picking one off a
   * flat list of their own names could not tell which they had chosen.
   */
  const targetOptions = [
    { value: ALL_CORRECT, label: t('task.flow.allCorrect.title') },
    ...rules.map((r, i) => ({
      value: `event:${r.id}`,
      label: r.title?.trim() || t('task.flow.rule.unknownBlock'),
      group: i === 0 ? t('task.flow.special.sectionTitle') : undefined,
    })),
    // The same name its own card wears: `title` when the teacher gave it one, the effect
    // sentence when they did not. A change with no effects yet names itself a prompt to add one.
    ...changes.map((c, i) => ({
      value: `change:${c.id}`,
      label: c.title?.trim() || changeTitle(c, allBlocks, t),
      group: i === 0 ? t('task.flow.changes.sectionTitle') : undefined,
    })),
  ];
  /* Deleting the rule a teacher was aiming at drops the aim back to All Correct rather than
     leaving the select showing a target that is gone. */
  const target = targetOptions.some(o => o.value === targetId) ? targetId : ALL_CORRECT;
  const targetConditions: FlowCondition[] =
    target.startsWith('event:') ? rules.find(r => r.id === target.slice(6))?.conditions ?? []
      : target.startsWith('change:') ? changes.find(c => c.id === target.slice(7))?.conditions ?? []
        : [];
  /** Which block each condition names, so a row can show what the target wants of it. */
  const wantedBy = new Map<string, string[]>();
  for (const c of targetConditions) {
    if (c.kind !== 'feedback') continue;
    wantedBy.set(c.blockId, [...(wantedBy.get(c.blockId) ?? []), c.stateId]);
  }

  /*
   * The tallies alone, now. The verdict line is gone: the chips below carry their own tone, so a
   * word reading "Incorrect" under a chip already drawn incorrect was the same fact twice, in
   * the row where space is scarcest.
   */
  const blockTallies = counts(taskId)?.visit.blocks ?? {};
  /*
   * ONLY WHAT A STUDENT CAN ANSWER.
   *
   * A task's prose and pictures have no attempts, no verdict and no key, so a row for each was
   * three empty columns and a name -- and on a task that is mostly explanation they pushed the
   * questions, which is the whole point of the band, off the bottom.
   */
  const taskBlocks = (tasks.find(x => x.id === taskId)?.blocks ?? [])
    .filter(b => (ANSWERABLE_BLOCK_TYPES as string[]).includes(b.type));
  /**
   * WHAT THE BLOCK IS WAITING FOR, as the chip its own test bar would draw.
   *
   * `stateChipsFor` is described in its own file as the master derivation every chip surface
   * reads -- the feedback panel, the test-bar legend and Flow all go through it. A teacher
   * already knows these chips; spelling the same condition a fourth way here would be a fourth
   * vocabulary for one idea, which is the thing that file exists to have stopped.
   *
   * The POSITIVE state, whichever of the three a block's mode gives it: `correct` when graded,
   * `any_answer` for a survey, `any_response` when optional. Those are the same three the
   * block's own positive slot switches between.
   */
  /**
   * WHAT THE BLOCK SAYS IT IS ASKING, where the block has said it.
   *
   * The only authority: which conditions are in force depends on the version on show, the roll,
   * and any scenario or branch the block resolved for itself, and deriving that out here is what
   * told a teacher the answer was Positive on a block whose scenario had swapped it. A block that
   * has not reported falls back to the derivation below, which still refuses to guess on anything
   * that can vary.
   */

  const positiveChips = (b: { type: string; data?: Record<string, unknown> | null }) => {
    if (!b.data || varies(b.data)) return null;
    const feedback = b.data.feedback as { states?: { trigger: string }[] } | undefined;
    const positive = (feedback?.states ?? [])
      .find(st => st.trigger === 'correct' || st.trigger === 'any_answer' || st.trigger === 'any_response');
    return positive ? stateChipsFor(b as never, positive as never, t, i18n.language) : null;
  };

  /**
   * THIS VISIT, not the session.
   *
   * The session window is deliberately not shown. Pressing Preview starts a fresh run, so on the
   * first visit the two windows carry the same number, and nothing a teacher authors reads the
   * session one — a readout that is usually a duplicate and never actionable earns no space.
   */
  const tally = counts(taskId)?.visit.task ?? NO_TALLY;
  const visit = visitCount(taskId);

  /* ONE NAME. The control in the bar says Inspect and so does the panel it opens; the old
     `debugHeading` read "Behind the Scenes" and implied something was broken rather than that
     a teacher is looking at how the task works. */
  const heading = t('task.bottomBar.inspect');

  const header = fullScreen ? (
    <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle px-4 py-2.5 text-base font-semibold text-ws-primary dark:bg-ws-text-link/[.20] dark:text-ws-text-link'>
      {/* The same glyph as the control that opened it. A bug sat here while the control was
          a bug too, and both said "something is broken" about a panel that describes how the
          task WORKS. */}
      <ScanEye aria-hidden='true' className='h-6 w-6' />
      <span className='flex-1'>{heading}</span>
      {onClose && (
        <IconButton size='lg' onClick={onClose} aria-label={t('task.bottomBar.debugClose')}
          className='text-current hover:bg-black/10 dark:hover:bg-white/10'>
          <X aria-hidden='true' className='h-5 w-5' />
        </IconButton>
      )}
    </div>
  ) : onClose ? (
    <div className='flex shrink-0 items-center gap-2'>
      <h2 className='flex-1 text-sm font-semibold text-ws-text'>{heading}</h2>
      {/* `lg` on a phone, where this is the landscape dock's only way out and a 28px X is a
          target a thumb misses. The same control on a desktop is a mouse's. */}
      <IconButton size={phone ? 'lg' : 'sm'} onClick={onClose} aria-label={t('task.bottomBar.debugClose')}>
        <X aria-hidden='true' className='h-4 w-4' />
      </IconButton>
    </div>
  ) : (
    /*
     * NO TITLE where the panel is simply the slot's occupant.
     *
     * It is the only thing in that column and nothing summoned it, so a heading announced what
     * the teacher was already looking at and charged a line of a narrow panel's height for it.
     * The phone sheet keeps its titlebar: there it IS the whole screen and needs the close.
     */
    null
  );

  const attemptsBar = (
    <>
      {/*
        ANCHORED, outside the scrolling area.
        A rule keyed to an attempt count is the one route whose cause is visible nowhere else in
        the panel, so a long block list must not be able to push the number that explains it off
        the bottom. The two controls that move these numbers sit with the numbers they move.
      */}
      <div className={`space-y-3 border-t border-ws-border pt-3 ${short ? '' : 'shrink-0'} ${fullScreen ? 'px-4 pb-4' : 'pb-0.5'}`}>
        {/*
          WRAPS RATHER THAN OVERFLOWS. In the 320px landscape dock the heading, the visit and two
          buttons wanted 280px of a 269px row: the buttons do not shrink, so "Visit 1" was
          crushed to 28px and Reset ran 11px past the panel's edge. Reported as smushed.
        */}
        <div className='flex flex-wrap items-center gap-2'>
          {/* A STEP UP FROM THE SECTIONS ABOVE. This bar is the panel's foot, not a fourth
              section: it never scrolls away, and at the section size its heading read as a
              label belonging to the two buttons beside it rather than to the numbers below. */}
          <h3 className={HEADING_CLASS_LG}>{t('task.bottomBar.debugAttempts')}</h3>
          {/* Zero while the run has not reached this task, which is a real state and reads
              correctly: no visit, no attempts. */}
          <span className='flex-1 whitespace-nowrap text-sm text-ws-muted'>{t('task.bottomBar.debugVisit', { n: visit })}</span>
          {/* THEIR OWN LINE ON A PHONE. At touch size the pair is wider than any phone panel
              leaves beside a heading, and a row that wraps mid-pair puts one button under the
              other. `basis-full` makes the break deliberate instead of incidental. */}
          <div className={`flex items-center gap-2 ${phone ? 'basis-full' : ''}`}>
            {onReroll && (
              <Button size={phone ? 'md' : 'sm'} className={phone ? 'min-h-11 flex-1' : ''}
                variant='secondary' icon={<Dices aria-hidden='true' />} onClick={onReroll}>
                {t('task.bottomBar.previewReroll')}
              </Button>
            )}
            {onReset && (
              <Button size={phone ? 'md' : 'sm'} className={phone ? 'min-h-11 flex-1' : ''}
                variant='secondary' icon={<RotateCcw aria-hidden='true' />} onClick={onReset}>
                {t('task.bottomBar.previewReset')}
              </Button>
            )}
          </div>
        </div>
        {/*
          ACROSS, on a line of their own, and the biggest type in the panel.
          Four numbers sharing the heading's line get squeezed into whatever the buttons leave
          and wrap to four rows -- taller than the tiles they replaced. Given the full width
          they fit on one.

          At `text-sm` they lost the row anyway: two bordered buttons directly above out-weighed
          the thing they exist to change, and the readout a teacher came to this bar for read as
          its caption. The numbers are the content here, so they are sized like it -- the labels
          stay small, because `ALL` and `UNIQUE` are read once and the digits are read every
          submit.
        */}
        {/*
          ONE LINE IN THE LANDSCAPE DOCK, which is the only mount that cannot hold the row.

          Measured: the four pairs want about 373px at the full treatment —
          ALL/COMPLETE/UNIQUE/INCORRECT is most of it — and the 320px dock leaves 304 inside its
          padding. So they wrapped to two rows in the layout with the least height to spare, and
          the bar grew by a line exactly where the connections and blocks above it could least
          afford one. Four savings bring it to about 294: the tracking goes, the gaps tighten, and
          both the digits and the labels drop a step.

          The portrait sheet has the width of the phone and keeps all of it — shrinking type that
          already fits buys nothing and costs legibility. It is CENTRED instead, because there the
          row is narrower than the screen it sits on and reads as abandoned against the left edge.

          Keyed to `short`, the MOUNT, rather than to `phone`, the device. The dock is 320px
          whatever is pointing at it, and the first attempt at this keyed to `phone` and so did
          nothing at all for a browser merely sized like one.
        */}
        <ul className={`flex flex-wrap items-baseline gap-y-1 ${tight ? 'gap-x-2' : 'gap-x-3.5'} ${fullScreen ? 'justify-center' : ''}`}>
          {FILTERS.map((f) => (
            <li key={f} className={`flex items-baseline ${tight ? 'gap-0.5' : 'gap-1'}`}>
              <span className={`font-bold leading-none tabular-nums text-ws-text ${tight ? 'text-sm' : 'text-lg'}`}>{tally[f]}</span>
              <span className={`uppercase text-ws-muted ${tight ? 'text-[11px]' : 'text-xs tracking-wide'}`}>
                {t(FILTER_KEYS[f])}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  return (
    <div className={`flex min-h-0 w-full flex-col ${fullScreen ? 'h-full' : 'h-full gap-3 overflow-hidden rounded-xl border border-ws-border bg-ws-surface p-3'}`}>
      {header}
      <div className={fullScreen ? 'flex min-h-0 flex-1 flex-col gap-3 p-4' : 'contents'}>
        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto pr-2'>

          {/*
            ABOVE THE MAP, because a recall is an input ARRIVING from somewhere else and that is
            what the band under it already says. It was going to be a column beside the task
            card; the card is 282 of the diagram's 360 and already inset from the rows above and
            below, so taking width from it would have cost the three bands their shared edges
            for a list that is usually two rows long.

            Only the ones nothing has walked to. A task whose recalls are all answered shows no
            band at all, which is the state a teacher walking the experience in order is in.
          */}
          {feeders.length > 0 && (
            <section className='space-y-2'>
              <h3 className={HEADING_CLASS}>{t('task.bottomBar.debugRecalls')}</h3>
              <ul className='divide-y divide-ws-border overflow-hidden rounded-lg border border-ws-border'>
                {feeders.map(f => {
                  /* The stand-in is stored as the SAMPLE, so the menu's value is the index that
                     produced it. Matching on the value rather than holding a cursor keeps the row
                     honest when a stand-in is cleared from the prose instead of from here. */
                  /* Set together, so they are read together: a merged row shows a value only
                     where every reference it speaks for holds the same one. They can disagree --
                     the chips in the prose still set one at a time -- and a row claiming an
                     answer two of its references do not have would be the wrong half of a lie. */
                  const held = f.aliasIds.map(id => JSON.stringify(standIns[id]));
                  const agreed = held.every(v => v === held[0]) ? held[0] : undefined;
                  const at = f.choices.findIndex(c => JSON.stringify(c.sample) === agreed);
                  /* The ring on the task and the row here mark the same thing, so they agree on
                     what "unset" is and wear the same colour. The menu already SAYS Not set; this
                     is so the band can be read at a glance beside a page full of dimmed names. */
                  const unset = f.aliasIds.some(id => standIns[id] === undefined);
                  return (
                    <li
                      key={f.aliasIds.join('|')}
                      className={`flex items-center gap-2 border-l-2 py-1.5 pl-2 pr-2.5 ${
                        unset ? 'border-ws-answer-ref-fg' : 'border-transparent'}`}
                      /* Hover and focus both, so the pointer is not the only way to ask what a
                         recall feeds -- tabbing into the row's menu lights the same blocks. */
                      onMouseEnter={() => onRecallHover?.(f.aliasIds[0]!)}
                      onMouseLeave={() => onRecallHover?.(null)}
                      onFocus={() => onRecallHover?.(f.aliasIds[0]!)}
                      onBlur={() => onRecallHover?.(null)}
                    >
                      <span className={`min-w-0 flex-1 truncate text-xs ${
                        unset ? 'text-ws-answer-ref-fg' : 'text-ws-text'}`}>{f.name}</span>
                      {f.choices.length === 0 ? (
                        /* NOTHING TO OFFER. `answerChoicesFor` enumerates no answer for an
                           ordering or a sort source, and a menu with one dead entry says less
                           than a word does. Named rather than hidden: the reference IS feeding
                           this task and a teacher looking for why it is blank deserves to find
                           it here rather than conclude the band missed it. */
                        <span className='shrink-0 text-xs italic text-ws-muted'>
                          {t('task.bottomBar.debugRecallNoChoices')}
                        </span>
                      ) : (
                        <SelectMenu
                          inline
                          touch={phone}
                          fullWidth
                          className='min-w-0 max-w-[60%]'
                          ariaLabel={t('task.bottomBar.debugRecallSet', { name: f.name })}
                          value={at >= 0 ? String(at) : ''}
                          onChange={v => f.aliasIds.forEach(
                            id => setStandIn?.(id, v === '' ? null : f.choices[Number(v)]!.sample))}
                          options={[
                            { value: '', label: t('task.bottomBar.debugRecallUnset') },
                            ...f.choices.map((c, i) => ({ value: String(i), label: c.label })),
                          ]}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* FIRST, because it is the only part that is about more than this task — a teacher
              opening the panel mid-experience is usually asking "where am I", not "how many
              tries has this had". */}
          <section className='space-y-2'>
            {/*
              NO FOLD. It was there to buy back 258px of a 452px column, on the theory that a
              teacher reads the map to orient and then wants it gone. They do not: the diagram is
              the live readout -- the rung that fires lights up as you type -- so folding it hid
              the one thing in the panel that moves, to save scrolling past the one thing worth
              scrolling to.
            */}
            <h3 className={HEADING_CLASS}>{t('task.bottomBar.debugConnections')}</h3>
            <PreviewConnectionsDiagram tasks={tasks} taskId={taskId} arrival={arrival} firedSlot={firedSlot} predictedSlot={predicted} />
          </section>

          {taskBlocks.length > 0 && (
            <section className='space-y-2'>
              <h3 className={HEADING_CLASS}>{t('task.bottomBar.debugBlocks')}</h3>
              {/*
                THE AIM SITS ON THE BAND IT CHANGES, not under the diagram where it started: it
                alters one thing, the second group of chips on every row below.

                IN WORDS, not an arrow. The arrow was the glyph those rows wear, on the theory
                that repeating it would read as the column's caption -- it did not. A menu whose
                only label is a symbol says nothing about what picking from it will do, and this
                one does something a teacher has to be told: it changes what the rows ask of
                them. The sentence runs into the menu and finishes there.

                `SelectMenu`, which is the task builder's dropdown -- not a native select. These
                options are the teacher's own event and change names, they come in two kinds
                that need headings, and a platform `<option>` can carry neither.
              */}
              {/*
                A SELECT WITH ONE OPTION IS NOT A CHOICE.
                With no special events and no changes — which is every task before Flow is turned
                on, and every task of a teacher who has not — this list holds All Correct alone.
                The control then asks a question with a single answer, and reads as an invitation
                to a feature the teacher has no way to reach. The LABEL stays either way: what the
                band below is measured against is the thing a beginner most needs to know, and it
                is what makes this panel a quick way to see why a task will not advance.
              */}
              <div className='flex items-baseline gap-1.5'>
                {/* Matched by the menu's `ariaLabel`, so a reader hears the sentence once rather
                    than meeting a loose fragment of it before the control. */}
                <span aria-hidden='true' className='shrink-0 text-xs text-ws-muted'>
                  {t('task.bottomBar.debugTarget')}
                </span>
                {targetOptions.length > 1 ? (
                  <SelectMenu
                    inline
                    touch={phone}
                    fullWidth
                    className='min-w-0 flex-1'
                    ariaLabel={t('task.bottomBar.debugTarget')}
                    value={target}
                    onChange={setTargetId}
                    options={targetOptions}
                  />
                ) : (
                  /* Named, not blank: the band beneath is measured against this and a reader
                     arriving at the rows needs to know what they are being measured against. */
                  <span className='min-w-0 flex-1 truncate text-xs font-medium text-ws-text'>
                    {targetOptions[0]!.label}
                  </span>
                )}
              </div>
              {/*
                A LIST, one line per block, and no disclosure on any of it.
                The full legend lives in the block's own test bar, which is where a block is
                debugged; what this band is for is driving flow, and for that a teacher needs
                the state each block is firing and nothing else. A card that opened onto the
                legend made this a second, worse copy of the test bar and cost four lines of a
                452px column to do it.
              */}
              <ul className='divide-y divide-ws-border overflow-hidden rounded-lg border border-ws-border'>
                {taskBlocks.map(b => {
                  const tally = blockTallies[b.id];
                  const meta = BLOCK_META[b.type];
                  const name = b.name?.trim() || (meta ? t(meta.labelKey) : b.type);
                  const Icon = meta?.Icon;
                  return (
                    <li key={b.id} className='flex items-center gap-2 px-2.5 py-1.5'>
                      {/*
                        THE NAME, DRAWN AS A GLYPH. It cost a row of its own to say "Numerical 1",
                        which tells a teacher reading Preview nothing the chip beside it does not.
                        The type icon says the same in the gutter for free and the rows are in
                        page order, so position says WHICH one. The name survives for a reader.
                      */}
                      {Icon && <Icon aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-muted' />}
                      <span className='sr-only'>{name}</span>
                      <div className='min-w-0 flex-1'>
                        <BlockConditions
                          chips={reported[b.id]}
                          fallback={positiveChips(b)}
                          unreachableLabel={t('blocks.answerRef.chipLabel.broken')}
                          /*
                            WHAT THE PINNED RUNG WANTS OF THIS BLOCK, where it wants anything.
                            Drawn beside what is firing rather than instead of it, so the gap
                            between the two IS the instruction: a row that already matches is
                            done, and one that does not is what to go and change.
                          */
                          wanted={wantedBy.get(b.id)}
                          wantPositive={target === ALL_CORRECT}
                        />
                      </div>
                      {/* RESERVED, not conditional: it appears on the first submit, and letting
                          it in and out moved every row below it. `invisible` is absent to a
                          reader and present to the layout. */}
                      <span className={`shrink-0 text-xs tabular-nums text-ws-muted ${tally ? '' : 'invisible'}`}>
                        &#215;{(tally ?? NO_TALLY).all}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

        {short && attemptsBar}
        </div>
      </div>
      {!short && attemptsBar}
    </div>
  );
}

/**
 * THE THREE NAMES ONE STATE GOES BY. `correct` when graded, `any_answer` for a survey,
 * `any_response` when optional -- the same three `isPositiveTrigger` holds and the same three a
 * block's own positive slot switches between. Spelling them here rather than asking which mode
 * the block is in is the point: the block has already picked one by reporting that chip.
 */
const POSITIVE_CATEGORIES: TestBarCategory[] = ['correct', 'any_answer', 'any_response'];

/**
 * Past this many terms on either side, the arrow stops being a hinge and becomes a divider.
 *
 * ONE. Not three, which is where this started: a term is a chip carrying authored text, and two
 * of them are already wide enough to wrap the row on a 340px column -- at which point the arrow
 * is somewhere in the middle of a paragraph rather than between two halves. FITB is the case
 * that showed it: its correct answer is a conjunction across blanks, so a two-blank question
 * has only two terms and wrapped anyway.
 *
 * Counting terms rather than measuring width on purpose. The width depends on the authored
 * words, the column, the font and the zoom, and a layout measured in an effect is a second
 * render and a frame of the wrong thing. One term against one term is the only shape that
 * reliably fits, so that is the one that stays inline.
 */
const STACK_AT = 1;

/**
 * THE TERMS THAT FIRED, without the legend's own punctuation between them.
 *
 * The legend's connectors belong to the CONDITION, and the condition's word is not this row's
 * word. Default Incorrect on a multiselect is a disjunction -- it fires if any wrong option is
 * ticked OR any right one is missing -- so the legend joins its terms with "or", correctly.
 * Narrowed to what is live, those terms are not alternatives at all: every one of them is true
 * of the answer on screen right now, simultaneously.
 *
 * So the row read "A or B or C -> X and Y and Z", which is two different logics facing each
 * other across one arrow, and a teacher building an answer against it is being told their picks
 * are alternatives while the target is a set. Reported as exactly that.
 *
 * The join is therefore the ROW's, not the chip's: see `AND_JOIN` at the render. The wanted side
 * keeps the block's own connectors, because that side really is the condition and a disjunctive
 * target genuinely means "any one of these".
 */
function activeTerms(chips: TestBarChip[]): TestBarChip[] {
  return chips.filter(c => c.kind !== 'connector' && c.active);
}

/**
 * ONE LINE: what this block is firing, and what a pinned rung wants of it.
 *
 * No labels and no disclosure. The row used to carry a label column ("Current", "Wanted") and
 * open onto the test bar's whole legend. Both went when the band's job narrowed to driving
 * flow: a block is debugged in its own test bar, which draws that legend properly, so drawing
 * it again here was a second and worse copy costing four lines of a 452px column.
 *
 * The two groups are told apart by a glyph rather than a word -- an arrow for a target not met,
 * a tick for one that is. Readers get the words instead, which is the right split: a sighted
 * teacher is scanning a dozen of these at once and a reader is hearing one at a time.
 */
function BlockConditions({ chips, fallback, unreachableLabel, wanted, wantPositive }: {
  chips: TestBarChip[] | undefined;
  fallback: ConditionChips | null;
  unreachableLabel: string;
  /** State ids the pinned rung needs from this block, if a rung is pinned and it names one. */
  wanted?: string[];
  /**
   * ALL CORRECT WANTS SOMETHING OF EVERY BLOCK, and names none of them.
   *
   * It is the rung a teacher pins most and the one `requires` is emptiest for: its test is
   * compiled in, so there are no `FlowCondition`s to pick a block out of. The target is the
   * same for all of them -- fire your positive state -- so the chip is chosen HERE, out of
   * what the block itself reported, rather than guessed at from block data upstairs.
   */
  wantPositive?: boolean;
}) {
  const { t } = useTranslation();
  if (!chips?.length) {
    return fallback ? <ConditionChipRow chips={fallback} unreachableLabel={unreachableLabel} /> : null;
  }

  const current = activeTerms(chips);
  /* Matched on the chip's own id, so a block that already satisfies the target shows the same
     chip in both groups -- which is how a teacher sees there is nothing to do to this one. */
  const want = wantPositive
    ? chips.filter(c => POSITIVE_CATEGORIES.includes(c.category))
    : wanted?.length ? chips.filter(c => wanted.includes(c.id)) : null;
  /*
   * CONNECTORS ARE NOT TERMS. A positive state spread over several chips carries the word that
   * joins them, and a connector is never `active` -- counting it would make `met` false on a
   * block that is fully satisfied, which is the one row a teacher needs to be able to stop
   * looking at.
   *
   * The length check is belt and braces rather than a reachable case: no derivation produces a
   * wanted set of connectors alone. It is there because `[].every()` is TRUE, and the one way
   * this must never fail is by telling a teacher a block is done when nothing was asked of it.
   */
  const terms = (want ?? []).filter(c => c.kind !== 'connector');
  const met = terms.length > 0 && terms.every(c => current.some(x => x.id === c.id));

  /*
   * THE ARROW GETS A LINE OF ITS OWN once either side is long.
   *
   * Inside a sentence of two or three chips it is a perfectly good hinge. On the 11-option
   * multiselect it is a 14px glyph adrift in forty, and the two halves run together into one
   * wall -- which is what the row was reported for. A full-width break above and below it makes
   * the division the first thing read rather than something to be found.
   *
   * `basis-full` on a zero-height span, because this is a wrapping flex row: it fills the line
   * and pushes what follows onto the next one, without the grid or the nested rows that would
   * cost this row its ability to reflow at all.
   */
  const stacked = Math.max(current.length, terms.length) > STACK_AT;
  const Break = () => <span aria-hidden='true' className='basis-full' />;

  return (
    <div className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1'>
      <span className='sr-only'>{t('task.bottomBar.debugCurrent')}</span>
      {/*
        An em dash rather than nothing, at a chip's height. A bare "-" in a numerical fires no
        state at all, so without it the line vanishes under the cursor -- and a line that comes
        and goes moves every row below it.
      */}
      {current.length === 0
        ? <span className='flex min-h-5 items-center text-xs text-ws-muted'>&#8212;</span>
        : current.map((c, i) => (
          <Fragment key={c.id}>
            {/*
              THE ROW'S OWN JOIN, not the chip's. Every term here is true of the answer on
              screen at the same moment, so they are joined by "and" whatever word the state
              they came from uses between its alternatives. See `activeTerms`.
            */}
            {i > 0 && <span className='shrink-0 text-xs text-ws-muted'>{t('blocks.connectors.and')}</span>}
            <LegendChip chip={c} compact />
          </Fragment>
        ))}
      {!!want?.length && (
        <>
          {stacked && <Break />}
          {/* `data-aim` so a reader of the DOM can tell the two glyphs apart. The icons differ
              only by their path data, which is not a thing a test can assert on. */}
          {met
            ? <Check data-aim='met' aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-success-fg' />
            : <ArrowRight data-aim='wanted' aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-muted' />}
          {stacked && <Break />}
          <span className='sr-only'>{t('task.bottomBar.debugWanted')}</span>
          {want.map(c => <LegendChip key={`want-${c.id}`} chip={c} compact />)}
        </>
      )}
    </div>
  );
}

/**
 * WHETHER WHAT THIS BLOCK ASKS CAN CHANGE UNDER THE STUDENT.
 *
 * Reported as a lie on the first real task it met: the panel read `data.feedback` -- the base --
 * and said "Correct is Positive" on a block whose scenario had swapped the answer. A block's
 * ACTIVE feedback is `scenarioRow.feedback ?? matchedBranch.feedback ?? data.feedback`, resolved
 * against the roll in force and the version on show, and every block does that resolution itself
 * with context this panel does not have. Reconstructing it here would be a second copy per block
 * of the thing that already disagreed once.
 *
 * So: where a block carries NONE of the three mechanisms that can move its answer, the base IS
 * the active one and the chip is safe. Where it carries any, the panel says nothing rather than
 * something it cannot stand behind. Conservative on purpose -- a missing chip costs a teacher a
 * glance at the block, and a wrong one costs them a submission.
 *
 * The real fix is the shadow report: a block that tells the panel what it is actually showing
 * cannot disagree with itself. This gate goes when that lands.
 */
function varies(data: Record<string, unknown>): boolean {
  const list = (k: string) => Array.isArray(data[k]) && (data[k] as unknown[]).length > 0;
  return list('versions') || list('scenarioBranches') || list('conditionalBranches')
    || (typeof data.conditionalEquation === 'string' && data.conditionalEquation.trim() !== '');
}

